import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import BdrSelectPopup from "../../../../components/BdrInput/BdrSelectPopup";
import { GET_DOOR_MATERIAL_RECIPES, GET_SHOP_INVENTORY, GET_SERVICE_ITEMS } from "../../../../utils/GraphQL/queries";
import { SAVE_DOOR_MATERIAL_RECIPE } from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useConfirm } from "../../../../utils/GlobalConfirm/GlobalConfirmContext";
import { gqlErrorMessage } from "../../../../utils/gqlErrorCode";
import { money, fmtDay } from "./doorMarginFormat";
import { contentsOf, defaultMode, qtyFromEntry, entryFromQty, describePerDoor, fmtQty, pluralize, unitNoun } from "../../../../utils/inventoryContents";

/**
 * Standard-cost recipe per door kind (plan §2.7). What one refinished (or
 * maintenance) door is EXPECTED to consume, in the stock units the ledger
 * moves (a gallon item → fraction of a gallon). Saving is always a NEW
 * version; snapshots pin the version they used, so old reports don't move.
 * The services item picked here is where the kind's catalog door price comes
 * from. Owns its own queries like SoftwareExpensesPanel.
 *
 * Usage units (2026-09-27, plans/recipe_usage_units_2026_09_27.md rev 2): each
 * line holds a canonical `qty` (stock units, a Number) plus an entry `mode` -
 * pieces per door (items with contents), stock units per door, or "1 unit
 * lasts N doors" - and the `text` typed in that mode. The estimate, the
 * per-door cell and the save read `qty` only, never `text`, so a mode switch
 * or a late catalog load can't reinterpret a number. The form waits for the
 * catalog before loading a saved version (mode is decided once, per line).
 */
const KINDS = [
  ["refinish", "Refinish"],
  ["maintenance", "Maintenance"],
];

const blankLine = () => ({ itemId: "", qty: null, mode: "stock", text: "" });

const RecipeEditor = ({ isAdmin }) => {
  const { handleSnack } = useSnackbar();
  const { confirm } = useConfirm();
  const [kind, setKind] = useState("refinish");
  const [lines, setLines] = useState([blankLine()]);
  const [servicesItemId, setServicesItemId] = useState("");
  const [note, setNote] = useState("");
  const [dirty, setDirty] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const { data, refetch } = useQuery(GET_DOOR_MATERIAL_RECIPES, { fetchPolicy: "network-only" });
  // network-only: contents edited in another tab must not convert with stale numbers.
  // includeInactive: a saved line can point at a deactivated item and keeps its pieces lens.
  const { data: invData } = useQuery(GET_SHOP_INVENTORY, { variables: { includeInactive: true }, fetchPolicy: "network-only" });
  const { data: svcData } = useQuery(GET_SERVICE_ITEMS, { fetchPolicy: "cache-first" });
  const [save, { loading: saving }] = useMutation(SAVE_DOOR_MATERIAL_RECIPE);

  const allItems = useMemo(
    () => [...(invData?.getInventory || [])].sort((a, b) => (a.itemName || "").localeCompare(b.itemName || "", undefined, { sensitivity: "base" })),
    [invData]
  );
  const catalogById = useMemo(() => Object.fromEntries(allItems.map((i) => [i.id, i])), [allItems]);
  // Pickers offer active items; an inactive item already on a line stays selectable for that line.
  const activeItems = useMemo(() => allItems.filter((i) => i.active !== false), [allItems]);
  const ready = !!data && !!invData;
  const services = useMemo(() => [...(svcData?.getServicesItems || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [svcData]);

  const versions = useMemo(() => (data?.getDoorMaterialRecipes || []).filter((r) => r.jobKind === kind), [data, kind]);
  const latest = versions[0] || null;

  // Load the latest version into the form whenever the kind (or data) changes
  // and the form isn't mid-edit. Waits for BOTH queries: the entry mode per
  // line depends on the item's contents.
  useEffect(() => {
    if (dirty || !ready) return;
    if (latest) {
      setLines(
        latest.lines.length
          ? latest.lines.map((l) => {
              const item = catalogById[l.itemId];
              const mode = defaultMode(item);
              const q = parseFloat(l.qtyPerDoor);
              const qty = Number.isFinite(q) && q > 0 ? q : null;
              return { itemId: l.itemId, qty, mode, text: entryFromQty(mode, qty, contentsOf(item)) };
            })
          : [blankLine()]
      );
      setServicesItemId(latest.servicesItemId || "");
      setNote("");
    } else {
      setLines([blankLine()]);
      setServicesItemId("");
      setNote("");
    }
  }, [latest, kind, dirty, ready, catalogById]);

  const setLine = (i, patch) => {
    setDirty(true);
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  };
  // Typing: text is what they see, qty is recomputed from it in the line's mode.
  const setLineText = (i, raw) => {
    const text = raw.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    const l = lines[i];
    const contents = contentsOf(catalogById[l.itemId]);
    const mode = l.mode === "pieces" && !contents ? "stock" : l.mode;
    setLine(i, { text, mode, qty: qtyFromEntry(mode, text, contents) });
  };
  // Switching mode re-displays the SAME qty; the number stored never changes,
  // so it is NOT an edit (no dirty flag - viewing in pieces mustn't enable Save).
  const setLineMode = (i, mode) => {
    const l = lines[i];
    const text = entryFromQty(mode, l.qty, contentsOf(catalogById[l.itemId]));
    setLines((ls) => ls.map((x, j) => (j === i ? { ...x, mode, text } : x)));
  };
  // A different item means a different unit - start the amount over.
  const setLineItem = (i, itemId) => {
    setLine(i, { itemId, qty: null, text: "", mode: defaultMode(catalogById[itemId]) });
  };
  const removeLine = (i) => {
    setDirty(true);
    setLines((ls) => (ls.length === 1 ? [blankLine()] : ls.filter((_, j) => j !== i)));
  };

  const estimate = lines.reduce((s, l) => {
    const it = catalogById[l.itemId];
    if (!it || !(l.qty > 0)) return s;
    return s + Math.round(l.qty * (parseInt(it.price, 10) || 0));
  }, 0);

  const onSave = async () => {
    if (!ready) return;
    // A row with an item but no usable amount ("0", ".", blank) would silently
    // drop out of the new version - refuse instead.
    const missing = lines.filter((l) => l.itemId && !(l.qty > 0));
    if (missing.length) {
      handleSnack(
        `${missing.length === 1 ? "1 line has" : `${missing.length} lines have`} an item but no amount - enter one or remove the line`,
        4000
      );
      return;
    }
    const clean = lines.filter((l) => l.itemId && l.qty > 0);
    if (!clean.length) {
      handleSnack("Add at least one line with a quantity per door", 3000);
      return;
    }
    try {
      await save({
        variables: {
          jobKind: kind,
          servicesItemId: servicesItemId || null,
          // Full precision; the server stores 6 significant figures.
          lines: clean.map((l) => ({ itemId: l.itemId, qtyPerDoor: String(l.qty) })),
          note: note.trim() || null,
        },
      });
    } catch (err) {
      handleSnack(`Save failed: ${gqlErrorMessage(err)}`, 5000);
      return;
    }
    // Saved. Reload the versions BEFORE clearing dirty, so the form reloads
    // the new version instead of flashing the old one. A failed reload is not
    // a failed save (and must not invite a second, duplicate version).
    let reloaded = true;
    try {
      await refetch();
    } catch (_) {
      reloaded = false;
    }
    setDirty(false);
    handleSnack(
      reloaded
        ? `${kind === "refinish" ? "Refinish" : "Maintenance"} recipe saved as a new version`
        : "Recipe saved, but the list didn't refresh - reload the page to see it",
      reloaded ? 3000 : 5000
    );
  };

  return (
    <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-med">
      <div className="dm-panel-head">
        <h3 className="bm-panel-title">Material recipe per door</h3>
        <div className="dm-toggle" role="tablist" aria-label="Kind">
          {KINDS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={kind === k}
              className={`dm-toggle-btn ${kind === k ? "dm-toggle-active" : ""}`}
              onClick={async () => {
                if (kind === k) return;
                if (dirty) {
                  const ok = await confirm({ title: "Discard edits?", message: "Unsaved recipe edits will be lost.", confirmLabel: "Discard" });
                  if (!ok) return;
                }
                setDirty(false);
                setKind(k);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <p className="bm-panel-note">
        Enter each line the way you think about it: <strong>pieces per door</strong> for items with contents set on the
        Inventory page (1.5 filters), <strong>stock units per door</strong> (0.25 gallon), or <strong>doors per unit</strong> (1 gallon lasts 4
        doors). It's saved in stock units either way. Saving creates a new version; past snapshots keep the version they used.
        {latest ? ` Current: v${latest.version} · ${fmtDay(latest.effectiveFrom)}${latest.createdByName ? ` · ${latest.createdByName}` : ""}.` : " No recipe yet."}
      </p>

      <label className="dm-field">
        <span>Door price comes from</span>
        <BdrSelectPopup
          id="dm-recipe-service"
          className="dm-select bdr-select-trigger"
          label="Catalog service"
          value={servicesItemId}
          disabled={!isAdmin}
          onChange={(e) => { setDirty(true); setServicesItemId(e.target.value); }}
          placeholder="— pick the catalog service —"
          options={services.map((s) => ({
            value: s.id,
            label: `${s.itemName} · ${money(s.price)}`,
          }))}
        />
      </label>

      <div className="bm-table-scroll">
        <table className="bm-table dm-recipe-table">
          <thead>
            <tr>
              <th>Inventory item</th>
              <th className="bm-num">Amount</th>
              <th>Unit</th>
              <th className="bm-num">Cost / stock unit</th>
              <th className="bm-num">Per door</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const it = catalogById[l.itemId];
              const contents = contentsOf(it);
              const unit = unitNoun(it?.stockUnit);
              // Pieces mode needs contents; if they vanished (catalog edited) show stock.
              const mode = l.mode === "pieces" && !contents ? "stock" : l.mode;
              // ...and then the text must be the stock number too, not the leftover piece count.
              const shownText = mode !== l.mode ? entryFromQty(mode, l.qty, contents) : l.text;
              const per = it && l.qty > 0 ? Math.round(l.qty * (parseInt(it.price, 10) || 0)) : null;
              const pickable = it && it.active === false ? [...activeItems, it] : activeItems;
              return (
                <tr key={i}>
                  <td>
                    {/* Merge 2026-09-28: the control is ours, everything it
                        does is main's — `setLineItem` (not `setLine`), the
                        `ready` gate, the per-line `pickable` list that keeps a
                        selected-but-inactive item visible, and the "(inactive)"
                        suffix. */}
                    <BdrSelectPopup
                      id={`dm-recipe-line-${i}`}
                      className="dm-select dm-select-wide bdr-select-trigger"
                      label="Inventory item"
                      value={l.itemId}
                      disabled={!isAdmin || !ready}
                      onChange={(e) => setLineItem(i, e.target.value)}
                      placeholder={ready ? "— choose item —" : "Loading…"}
                      options={pickable.map((c) => ({
                        value: c.id,
                        label: `${c.itemName}${
                          c.active === false ? " (inactive)" : ""
                        }`,
                      }))}
                    />
                  </td>
                  <td className="bm-num">
                    <input
                      className="dm-input dm-input-num"
                      value={shownText}
                      disabled={!isAdmin || !it}
                      inputMode="decimal"
                      placeholder={mode === "pieces" ? "1.5" : mode === "lasts" ? "4" : "0.25"}
                      aria-label={`Amount for ${it?.itemName || "line"}`}
                      onChange={(e) => setLineText(i, e.target.value)}
                    />
                    {it && l.qty > 0 && mode !== "stock" && (
                      <div className="dm-qty-hint">= {fmtQty(l.qty)} {unit} / door</div>
                    )}
                    {it && isAdmin && !(l.qty > 0) && <div className="dm-qty-hint dm-qty-hint-warn">enter an amount</div>}
                  </td>
                  <td>
                    <select
                      className="dm-select dm-select-mode"
                      value={mode}
                      disabled={!it}
                      aria-label={`Entry unit for ${it?.itemName || "line"}`}
                      onChange={(e) => setLineMode(i, e.target.value)}
                    >
                      {contents && <option value="pieces">{pluralize(contents.name, 2)} / door</option>}
                      <option value="stock">{pluralize(unit, 2)} / door</option>
                      <option value="lasts">doors per {unit}</option>
                    </select>
                  </td>
                  <td className="bm-num">{it ? `${money(it.price)} / ${unit}` : "--"}</td>
                  <td className="bm-num bm-money">{money(per)}</td>
                  <td>
                    {isAdmin && (
                      <button type="button" className="dm-link dm-link-danger" onClick={() => removeLine(i)} title="Remove line">
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4}>
                {isAdmin && (
                  <button type="button" className="dm-link" onClick={() => { setDirty(true); setLines((ls) => [...ls, blankLine()]); }}>
                    + Add line
                  </button>
                )}
              </td>
              <td className="bm-num bm-money">{money(estimate)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {isAdmin && (
        <div className="dm-form-row">
          <input className="dm-input" placeholder="Note for this version (optional)" value={note} onChange={(e) => { setDirty(true); setNote(e.target.value); }} />
          <button type="button" className="shop-inv-modal-save-btn bdr-grad-cal-green" disabled={saving || !dirty || !ready} onClick={onSave}>
            {saving ? "Saving…" : `Save as v${latest ? latest.version + 1 : 1}`}
          </button>
        </div>
      )}

      {versions.length > 0 && (
        <div className="dm-history">
          <button type="button" className="dm-link" onClick={() => setShowHistory((s) => !s)}>
            {showHistory ? "Hide versions" : `Versions (${versions.length})`}
          </button>
          {showHistory && (
            <ul className="dm-history-list">
              {versions.map((v) => (
                <li key={v.id}>
                  <strong>v{v.version}</strong> · {fmtDay(v.effectiveFrom)}
                  {v.createdByName ? ` · ${v.createdByName}` : ""}
                  {v.servicesItemName ? ` · price from ${v.servicesItemName}` : ""}
                  {v.note ? ` · ${v.note}` : ""}
                  <span className="bm-muted">
                    {" "}
                    — {v.lines.map((l) => `${l.itemName} ${describePerDoor(l.qtyPerDoor, catalogById[l.itemId]?.stockUnit, contentsOf(catalogById[l.itemId]))}`).join(", ")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default RecipeEditor;
